// src\sections\auth\SessionTimer.tsx

import { useLazyMeQuery, useLogoutMutation, useRefreshAuthTokenMutation } from 'api/auth';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog';
import { SESSION_TIMEOUT_WARNING_MINS } from 'config';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { RootState } from 'store';
import { openDefaultErrorSnackbar, openDefaultSuccessSnackbar } from 'store/reducers/snackbar';
import { setLocalStorageItems } from 'utils/fileUtils';
import { Stack, Typography } from '@mui/material';
import { notifySessionChange } from 'utils/authUtils';

const warningTime = SESSION_TIMEOUT_WARNING_MINS * 60 * 1000;
const events = ['mousedown', 'mouseup', 'click', 'dblclick', 'wheel', 'keydown', 'keyup', 'scroll', 'touchstart', 'touchend'];
const LOCAL_STORAGE_CLOSE_TIME_KEY = 'browserCloseTime';
let resetTimerFunction: (() => void) | null = null; // Global variable to hold the resetTimer function

/**
 * SessionTimer component monitors user activity and triggers session timeout warnings.
 *
 * - Listens for user activities and resets the session timer on interaction.
 * - Displays a warning dialog when the session is about to expire.
 * - Allows the user to extend the session by refreshing the auth token.
 * - Handles session expiration and logout if the user is inactive.
 */
const SessionTimer = () => {
  const dispatch = useDispatch();

  const [showWarning, setShowWarning] = useState(false);
  const idleTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const decodedAuthToken = useSelector((state: RootState) => state.auth.decodedAuthToken);
  const lastActiveAt = useRef<number>(Date.now());
  const location = useLocation();

  const tokenExpiryDuration = useMemo(() => {
    if (!decodedAuthToken) {
      return null;
    }
    const { iat, exp } = decodedAuthToken;
    return (exp - iat) * 1000; // Convert unix timestamp in seconds to milliseconds
    // return 180 * 1000; // For mock the popup
  }, [decodedAuthToken]);

  const [logout] = useLogoutMutation();
  const [refreshAuthToken] = useRefreshAuthTokenMutation();
  const [fetchMeInfo] = useLazyMeQuery();

  /**
   * Logs out the user when the session is manually canceled.
   */
  const onCancel = useCallback(async () => {
    if (decodedAuthToken) {
      setLocalStorageItems({
        user_id: decodedAuthToken.nameid,
        last_location: location.pathname
      });
    }
    localStorage.setItem('isLoggedOut', 'true');
    await logout();
    const lastCloseTime = localStorage.getItem(LOCAL_STORAGE_CLOSE_TIME_KEY);
    if (lastCloseTime) {
      localStorage.removeItem(LOCAL_STORAGE_CLOSE_TIME_KEY);
    }
    dispatch(
      openDefaultSuccessSnackbar({
        message: 'Your session is logged out successfully',
        preventAutoDismiss: true,
        hideDuration: 15000
      })
    );
  }, [dispatch, logout, location.pathname]);

  /**
   * Handles session expiration due to inactivity.
   * Logs out the user and displays an error message.
   */
  const onSessionExpired = useCallback(async () => {
    if (decodedAuthToken) {
      setLocalStorageItems({
        user_id: decodedAuthToken.nameid,
        last_location: location.pathname
      });
    }
    localStorage.setItem('isLoggedOut', 'true');
    await logout();
    const lastCloseTime = localStorage.getItem(LOCAL_STORAGE_CLOSE_TIME_KEY);
    if (lastCloseTime) {
      localStorage.removeItem(LOCAL_STORAGE_CLOSE_TIME_KEY);
    }
    dispatch(
      openDefaultErrorSnackbar({
        message: 'Your session has expired due to inactivity. Please log in again to continue.',
        preventAutoDismiss: true,
        hideDuration: 15000
      })
    );
  }, [dispatch, logout, location.pathname]);

  /**
   * Handles the session timeout logic.
   * Determines if the session has expired or if a warning should be displayed.
   */
  const timeoutHandler = useCallback(() => {
    const _now = Date.now();
    // console.info(`!!!!! IDLE TIMER: timeout handler triggered at: ${new Date(_now)}`);

    const startTimer = (duration: number) => {
      // console.info(`!!!!! IDLE TIMER: start timer for duration: ${duration}`);
      clearTimer();
      idleTimeoutIdRef.current = setTimeout(timeoutHandler, duration);
    };

    if (tokenExpiryDuration == null) {
      return;
    }

    const durationFromLastActiveAt = _now - lastActiveAt.current;
    // console.info(`!!!!! IDLE TIMER: durationFromLastActiveAt: ${durationFromLastActiveAt}`);
    // console.info(`!!!!! IDLE TIMER: tokenExpiryDuration: ${tokenExpiryDuration}`);

    if (durationFromLastActiveAt >= tokenExpiryDuration) {
      // console.info('!!!!! IDLE TIMER: session expired');
      onSessionExpired();
    } else {
      let timerDuration = tokenExpiryDuration - warningTime;
      let _durationDiff = tokenExpiryDuration - durationFromLastActiveAt;
      // console.info(`!!!!! IDLE TIMER: _durationDiff: ${_durationDiff}`);

      if (_durationDiff <= warningTime || _durationDiff - warningTime < 100) {
        // console.info('!!!!! IDLE TIMER: show warning ');
        setShowWarning(true);
        timerDuration = warningTime;
      }
      startTimer(timerDuration);
    }
  }, [onSessionExpired, tokenExpiryDuration]);

  /**
   * Clears the current session timer.
   */
  const clearTimer = () => {
    if (idleTimeoutIdRef.current) clearTimeout(idleTimeoutIdRef.current);
  };

  /**
   * Resets the session timer, extending the session duration.
   * Used to keep the session active when the user interacts with the app.
   */
  const resetTimer = useCallback(() => {
    if (tokenExpiryDuration == null) {
      return;
    }
    // console.info('!!!!! IDLE TIMER: reset timer');
    clearTimer();
    const timerDuration = tokenExpiryDuration - warningTime;
    idleTimeoutIdRef.current = setTimeout(timeoutHandler, timerDuration);
  }, [timeoutHandler, tokenExpiryDuration]);

  /**
   * Resets the session timer on user activity.
   */
  const _reset = useCallback(
    (e?: any) => {
      lastActiveAt.current = Date.now();
      resetTimer();

      // Send message to other tabs
      window.localStorage.setItem('lastActiveAt', lastActiveAt.current.toString());
      window.dispatchEvent(new Event('storage'));
    },
    [resetTimer]
  );

  const verifyLastCloseTime = useCallback(() => {
    const lastCloseTime = localStorage.getItem(LOCAL_STORAGE_CLOSE_TIME_KEY);
    if (lastCloseTime) {
      const lastCloseTimestamp = parseInt(lastCloseTime, 10);
      const now = Date.now();
      const twentyMinutesInMs = 20 * 60 * 1000;
      if (now - lastCloseTimestamp > twentyMinutesInMs) {
        onSessionExpired();
      }
      // Clear the localStorage after checking
      localStorage.removeItem(LOCAL_STORAGE_CLOSE_TIME_KEY);
    }
  }, [onSessionExpired]);
  useEffect(() => {
    resetTimerFunction = resetTimer; // Assign resetTimer to the global variable
    return () => {
      resetTimerFunction = null; // Clean up on unmount
    };
  }, [resetTimer]);

  useEffect(() => {
    verifyLastCloseTime();
  }, [verifyLastCloseTime]);

  /**
   * Handles user activity to reset the session timer.
   * Does nothing if the warning dialog is open.
   */
  const onUserActivity = useCallback(
    (e: any) => {
      if (showWarning) {
        return;
      }
      _reset(e);
    },
    [_reset, showWarning]
  );

  /**
   * Continues the session by refreshing the auth token.
   * Resets the session timer after the token is refreshed.
   */
  const onContinue = useCallback(async () => {
    await refreshAuthToken().unwrap();
    await fetchMeInfo().unwrap();
    setShowWarning(false);
    _reset();
    lastActiveAt.current = Date.now();
    localStorage.setItem('lastActiveAt', lastActiveAt.current.toString());
    window.dispatchEvent(new Event('storage'));
    notifySessionChange('reset');
  }, [_reset, refreshAuthToken]);

  useEffect(() => {
    timeoutHandler();

    events.forEach((_event) => window.addEventListener(_event, onUserActivity));

    return () => {
      clearTimer();
      events.forEach((_event) => window.removeEventListener(_event, onUserActivity));
    };
  }, [onUserActivity, timeoutHandler]);
  useEffect(() => {
    const handleBeforeUnload = () => {
      const now = Date.now().toString();
      localStorage.setItem(LOCAL_STORAGE_CLOSE_TIME_KEY, now);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'lastActiveAt' && event.newValue) {
        lastActiveAt.current = parseInt(event.newValue, 10);
        resetTimer();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [resetTimer]);

  const processEvent = (type: 'reset') => {
    if (type === 'reset') {
      _reset();
      setShowWarning(false);
    }
  };

  useEffect(() => {
    if ('BroadcastChannel' in window) {
      const authChannel = new BroadcastChannel('session');
      authChannel.addEventListener('message', (event) => processEvent(event.data.type));

      return () => {
        authChannel.removeEventListener('message', (event) => processEvent(event.data.type));
        authChannel.close();
      };
    }
  }, []);

  return (
    <ConfirmationDialog
      open={showWarning}
      hideCloseIcon={true}
      message="Session Expiry"
      additionalContent={
        <Stack gap={2}>
          <Typography>Your session is about to expire.</Typography>
          <Typography>Do you like to continue this session?</Typography>
        </Stack>
      }
      confirmButtonLabel="Yes"
      cancelButtonLabel="No"
      onConfirm={onContinue}
      onClose={onCancel}
      titleIconType="info"
    />
  );
};

/**
 * Exposed function to reset the session timer from outside the SessionTimer component.
 * This function can be used to keep the session alive during long-running operations,
 * such as file uploads or other background processes.
 */
export const resetSessionTimer = () => {
  if (resetTimerFunction) {
    resetTimerFunction(); // Expose the resetTimer function
  }
};

export default SessionTimer;
